import {MainAppBar} from "./app-bar";
import React, {useEffect, useState} from "react";
import {createMuiTheme} from "@material-ui/core/styles";
import {ThemeProvider} from '@material-ui/styles';
import {SettingsDrawer} from "./settings-drawer";
import {AssetDrawer} from "./asset-drawer";
import {ProjectionsManager} from "./projections/manager";
import {ImageAlignerAdvanced, ImageAlignerBasic} from "./alignment";
import {ImageLabelling} from "./labelling/image-labelling";
import {SceneViewer} from "./viewer/viewer";
import {Backdrop, Box, CircularProgress, createStyles, makeStyles, Theme} from "@material-ui/core";
import {useDispatch, useSelector} from 'react-redux';
import {addAssetFolder, isAssetFile} from './asset-utils';
import {changeDrawerOpen, changeSelectedTab, selectDrawerOpen, selectSelectedTab} from "./redux/settings-slice";
import {useSnackbar} from 'notistack';
import {app, dialog, isDevMode, isStaticSite, remote} from "./electron-modules";
import path from 'path';
import slash from "slash";
import {loadProjectFile, loadProjectJSON, projectFileType} from "./project-utils";
import {StaticExporter} from "./static-export";
import {PythonExecutable, WorkflowExecutable} from "./executable";

export const headerHeight = 48;
export const drawerWidth = 280;

// NOTE: Custom theme colors work but need to be manually added
declare module "@material-ui/core/styles/createPalette" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Palette {
    custom: Palette['primary'];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PaletteOptions {
    custom: PaletteOptions['primary'];
  }
}

const theme = createMuiTheme({
  zIndex: {
    appBar: 1100,
    modal: 1250,
    drawer: 1200,
    snackbar: 1400,
    tooltip: 1500
  },
  overrides: {
    MuiPopover: {
      root: {
        "z-index": "1300 !important"
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      },
      arrow: {
        "&::before": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          border: "0px"
        }
      },
    }
  },
  palette: {
    primary: {
      main: '#162031'
    },
    secondary: {
      main: '#dd0c29'
    },
    custom: {
      main: '#5c6ac4',
    },
  },
  mixins: {
    toolbar: {
      // override default
    }
  }
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      color: '#fff',
      zIndex: theme.zIndex.snackbar + 1,
      flexDirection: "column",
    },
    backdropText: {
      padding: theme.spacing(1)
    }
  }),
);

const DragDropLoader = (props) => {
  const {setAssetDrawerState, assetDrawerState, addFilePathsFunc} = props;

  const handleDrop = async event => {
    if (isStaticSite) {
      return;
    }

    const files = [...event.dataTransfer.files]
      .map(file => slash(file.path));

    addFilePathsFunc(files);
  };

  const handleDragOver = event => {
    if (isStaticSite) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (!assetDrawerState) {
      // Only trigger change if the draw was closed. This will avoid
      // performance issues due to calling the dispatch function too often
      setAssetDrawerState(true);
    }
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {props.children}
    </div>
  );
};

const BackdropToggle = () => {
  const classes = useStyles(theme);
  const [backdropOpen, setBackdropOpen] = useState(false);

  const handleClose = () => {
    setBackdropOpen(false);
  };

  const handleOpen = () => {
    setBackdropOpen(true);
  };

  return (
    <React.Fragment>
      <button
        hidden
        type="button"
        id="backdrop-show"
        onClick={handleOpen}
      />

      <button
        hidden
        type="button"
        id="backdrop-hide"
        onClick={handleClose}
      />

      <Backdrop
        open={backdropOpen}
        className={classes.backdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </React.Fragment>
  );
};

export const App = () => {
  const dispatch = useDispatch();

  const projectDrawerState = useSelector(selectDrawerOpen);
  const projectActiveTab = useSelector(selectSelectedTab);
  const showMessage = useSnackbar().enqueueSnackbar;

  const [viewer, setViewer] = useState(null);
  const [assetDrawerState, setDrawerState] = useState(false);
  const [settingsDrawerState, setSettingsDrawerState] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [singleAlignment, setSingleAlignment] = useState([0, 0, 0]);
  const [activeTab, setSelectedTab] = useState(0);
  const [tabsEnabled, setTabsEnabled] = useState(true);
  const [addObservations, setAddObservations] = useState(false);
  const [observationType, setObservationType] = useState("none");
  const [categoryList, setCategoryList] = useState([]);
  const [trainingArea, setTrainingArea] = useState([]);
  const [alignerAdvOpen, setAlignerAdvOpen] = useState(false);
  const [alignerBasicOpen, setAlignerBasicOpen] = useState(false);
  const [imageLabelOpen, setImageLabelOpen] = useState(false);
  const [projectionsOpen, setProjectionsOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const setDefaultTab = () => {
    setTabValue(0);
  };

  const setTabValue = (value) => {
    dispatch(changeSelectedTab(value));
  };

  const setAssetDrawerState = (state) => {
    dispatch(changeDrawerOpen(state));
  };

  const resetObservationType = () => {
    setObservationType("none");
  };



  const getAllFileArgs = () => {
    try {

      let rawArgs = remote.process.argv;
      let fileArgs = rawArgs.slice(1, rawArgs.length);
      let args = fileArgs.map((arg) => slash(arg.toString()));
      return args;
    } catch {
      return null;
    }
  };

  const addFilePaths = files => {
    if (!files || files.length === 0) {
      return;
    }

    const configs = files.filter(x => {
      return path.extname(x) === `.${projectFileType}`;
    });

    const assets = files.filter(x => isAssetFile(x));

    if (configs.length > 0) {
      // Load first config file (ignore other files)
      const success = loadProjectFile(configs[0]);
      if (success) {
        showMessage("Load project config file", {
          variant: "success"
        });
      }
      return;
    }

    if (assets.length > 0) {
      // Add new assets
      addAssetFolder(showMessage, dispatch, assets);
    }
  };

  /** Load geospatial file from launch arguments */
  const loadFromCompiledExe = () => {
    let paths = getAllFileArgs();

    // Load files from command line. addFilePaths
    // makes sure that invalid files will be ignored
    addFilePaths(paths);
  };

  /** Load project json from relative location */
  const loadFromStaticSite = () => {
    console.log("Load static site project config file");

    const cache = Date.now();
    fetch(`./static.json?cache=${cache}`)
      .then(res => res.text())
      .then(response => {
        loadProjectJSON(response);
      })
      .catch(() => {
        console.error("Error reading static website config");
      });
  };

  /** Close all active tool modals */
  const closeAllTools = () => {
    setAlignerAdvOpen(false);
    setAlignerBasicOpen(false);
    setImageLabelOpen(false);
    setProjectionsOpen(false);
    setExportModalOpen(false);
  };

  useEffect(() => {
    if (isDevMode || isStaticSite) {
      return;
    }

    new PythonExecutable().run({
      command: ['--check-tool', 'viewer'],
      onLine: jsonData => {
        if (!jsonData.available) {
          dialog.showMessageBox({
            title: 'Invalid License',
            message: 'This product is not licensed. If you have a valid license key, please open SOLV3D Engine and activate your license using the License Manager.',
            buttons: ['Exit', 'Open Engine'],
            type: 'error',
            noLink: true
          }).then(selection => {
            if (selection.response === 1) {
              new WorkflowExecutable().run({command: []});
            }

            app.quit();
          });
        }
      },
    });
  }, []);

  useEffect(() => {
    if (isStaticSite) {
      // Block browser context menu
      window.oncontextmenu = event => {
        event.preventDefault();
      };

      loadFromStaticSite();
    } else {
      loadFromCompiledExe();
    }
  }, []);

  useEffect(() => {
    setDrawerState(projectDrawerState);
  }, [projectDrawerState]);

  useEffect(() => {
    setSelectedTab(projectActiveTab);
  }, [projectActiveTab]);

  useEffect(() => {
    let width = settingsDrawerState ? drawerWidth : 0;
    viewer?.updateCompassOffset(width);
  }, [viewer, settingsDrawerState]);

  return (
    <ThemeProvider theme={theme}>
      <DragDropLoader
        setAssetDrawerState={setAssetDrawerState}
        assetDrawerState={assetDrawerState}
        addFilePathsFunc={addFilePaths}
      >
        {(!isStaticSite) && (<StaticExporter
          exportModalOpen={exportModalOpen}
          setExportModalOpen={setExportModalOpen}
        />)}

        <BackdropToggle/>

        <AssetDrawer
          viewer={viewer}
          measurements={measurements}
          assetDrawerState={assetDrawerState}
          setAssetDrawerState={setAssetDrawerState}
          activeTab={activeTab}
          tabsEnabled={tabsEnabled}
          setTabValue={setTabValue}
          setDefaultTab={setDefaultTab}
        />

        <SettingsDrawer
          viewer={viewer}
          open={settingsDrawerState}
          handleClose={() => {
            setSettingsDrawerState(false);
          }}
        />

        <ProjectionsManager
          projectionsOpen={projectionsOpen}
          setProjectionsOpen={setProjectionsOpen}
        />

        <ImageAlignerAdvanced
          viewer={viewer}
          addObservations={addObservations}
          setDefaultTab={setDefaultTab}
          setTabsEnabled={setTabsEnabled}
          observationType={observationType}
          setObservationType={setObservationType}
          alignerAdvOpen={alignerAdvOpen}
          alignerBasicOpen={alignerBasicOpen}
          setAlignerAdvOpen={setAlignerAdvOpen}
        />

        <ImageAlignerBasic
          viewer={viewer}
          setDefaultTab={setDefaultTab}
          setTabsEnabled={setTabsEnabled}
          singleAlignment={singleAlignment}
          alignerAdvOpen={alignerAdvOpen}
          alignerBasicOpen={alignerBasicOpen}
          setAlignerBasicOpen={setAlignerBasicOpen}
        />

        <ImageLabelling
          viewer={viewer}
          setDefaultTab={setDefaultTab}
          setTabsEnabled={setTabsEnabled}
          categoryList={categoryList}
          trainingArea={trainingArea}
          imageLabelOpen={imageLabelOpen}
          setImageLabelOpen={setImageLabelOpen}
        />

        <Box
          height="100vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <MainAppBar
            viewer={viewer}
            handleAssetDrawerOpen={() => {
              setAssetDrawerState(true);
            }}
            handleSettingsDrawerOpen={() => {
              setSettingsDrawerState(true);
            }}
            assetDrawerState={assetDrawerState}
          />

          <SceneViewer
            viewer={viewer}
            setViewer={setViewer}
            assetDrawerState={assetDrawerState}
            setAddObservations={setAddObservations}
            resetObservationType={resetObservationType}
            setMeasurements={setMeasurements}
            setSingleAlignment={setSingleAlignment}
            setCategoryList={setCategoryList}
            setTrainingArea={setTrainingArea}
            closeAllTools={closeAllTools}
          />

          {/* Debug stats panel */}
          <div id="debug-placeholder" style={{
            textAlign: "center",
            zIndex: 1300,
            margin: "0 auto",
            width: "fit-content"
          }}></div>

        </Box>
      </DragDropLoader>
    </ThemeProvider>
  );
};

export const showBackdrop = () => {
  const button = document.getElementById("backdrop-show");
  button.click();
};

export const hideBackdrop = () => {
  const button = document.getElementById("backdrop-hide");
  button.click();
};